<template>
  <v-sheet color="transparent">
    <v-toolbar v-if="generalData">
      <v-toolbar-title>
        {{ generalData.name }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn dark color="indigo" @click="$router.go(-1)">
        {{ $t('BUTTON.BACK') }}
      </v-btn>
    </v-toolbar>

    <v-card v-if="generalData" id="general" class="mt-3" outlined>
      <v-card-title class="pb-0">
        {{ $t('APPS.GENERAL_DATA') }}
        <v-btn
          v-if="!generalDataEditing"
          small
          icon
          class="ml-2"
          @click="generalDataEditing = true"
        >
          <v-icon color="indigo" v-text="'mdi-square-edit-outline'" />
        </v-btn>
        <v-spacer></v-spacer>
        <div v-if="!generalDataEditing">
          <v-btn
            v-if="typeof generalData.icon == 'string'"
            color="indigo"
            :href="generateUrl('icon')"
            target="_blank"
            outlined
            class="text-none font-weight-regular ml-4"
          >
            <v-img width="20" :src="generateUrl('icon')" class="mr-2" />
            {{ $t('ITEM.SHOW') }} {{ $t('APPS.ICON') }}
          </v-btn>
          <v-btn
            v-if="typeof generalData.splash == 'string'"
            color="indigo"
            :href="generateUrl('splash')"
            target="_blank"
            outlined
            class="text-none font-weight-regular ml-4"
          >
            <v-img width="20" :src="generateUrl('splash')" class="mr-2" />
            {{ $t('ITEM.SHOW') }} {{ $t('APPS.SPLASH') }}
          </v-btn>
          <v-btn
            v-if="typeof generalData.login_screen_image == 'string'"
            color="indigo"
            :href="generateUrl('login_screen_image')"
            target="_blank"
            outlined
            class="text-none font-weight-regular ml-4"
          >
            <v-img
              width="20"
              :src="generateUrl('login_screen_image')"
              class="mr-2"
            />
            {{ $t('ITEM.SHOW') }} {{ $t('APPS.LOGIN_IMAGE') }}
          </v-btn>
        </div>
        <div v-else>
          <v-btn
            v-if="typeof generalData.icon == 'string'"
            color="indigo"
            target="_blank"
            outlined
            class="text-none font-weight-regular ml-4"
            @click="removeFile('icon')"
          >
            <v-img width="20" :src="generateUrl('icon')" class="mr-2" />
            {{ $t('BUTTON.REMOVE') }} {{ $t('APPS.ICON') }}
          </v-btn>
          <v-btn
            v-if="typeof generalData.splash == 'string'"
            color="indigo"
            target="_blank"
            outlined
            class="text-none font-weight-regular ml-4"
            @click="removeFile('splash')"
          >
            <v-img width="20" :src="generateUrl('splash')" class="mr-2" />
            {{ $t('BUTTON.REMOVE') }} {{ $t('APPS.SPLASH') }}
          </v-btn>
          <v-btn
            v-if="typeof generalData.login_screen_image == 'string'"
            color="indigo"
            target="_blank"
            outlined
            class="text-none font-weight-regular ml-4"
            @click="removeFile('login_screen_image')"
          >
            <v-img
              width="20"
              :src="generateUrl('login_screen_image')"
              class="mr-2"
            />
            {{ $t('BUTTON.REMOVE') }} {{ $t('APPS.LOGIN_IMAGE') }}
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text style="position: relative;">
        <v-overlay v-if="!generalDataEditing" color="transparent" absolute />
        <alert-error class="mt-4" :error="errors" />
        <app-general-form v-model="generalData" :editing="generalDataEditing">
          <template v-if="generalDataEditing" v-slot:buttons="{ valid }">
            <v-btn
              class="mt-2"
              color="grey"
              outlined
              @click.prevent="cancelEditing('generalDataEditing')"
            >
              {{ $t('BUTTON.CANCEL') }}
            </v-btn>
            <v-btn
              class="ml-2 mt-2"
              color="primary"
              :disabled="!valid"
              @click.prevent="updateGeneralData"
            >
              {{ $t('BUTTON.UPDATE') }}
            </v-btn>
          </template>
        </app-general-form>
      </v-card-text>
    </v-card>

    <v-card v-if="layoutData" id="layout" class="mt-3" outlined>
      <v-card-title class="pb-0">
        {{ $t('APPS.LAYOUT_DATA') }}
        <v-btn
          v-if="!layoutDataEditing"
          small
          icon
          class="ml-2"
          @click="layoutDataEditing = true"
        >
          <v-icon color="indigo" v-text="'mdi-square-edit-outline'" />
        </v-btn>
      </v-card-title>
      <v-card-text style="position: relative;">
        <v-overlay v-if="!layoutDataEditing" color="transparent" absolute />
        <app-layout-form v-model="layoutData">
          <template v-if="layoutDataEditing" v-slot:buttons="{ valid }">
            <v-btn
              class="mt-2"
              color="grey"
              outlined
              @click.prevent="cancelEditing('layoutDataEditing')"
            >
              {{ $t('BUTTON.CANCEL') }}
            </v-btn>
            <v-btn
              class="ml-2 mt-2"
              color="primary"
              :disabled="!valid"
              @click.prevent="updateLayoutData"
            >
              {{ $t('BUTTON.UPDATE') }}
            </v-btn>
          </template>
        </app-layout-form>
      </v-card-text>
    </v-card>
  </v-sheet>
</template>

<script>
export default {
  name: 'AppsShowPage',

  components: {
    AlertError: () => import('@/modules/_global/_components/AlertError'),
    AppGeneralForm: () =>
      import('@/modules/apps/components/AppGeneralForm.vue'),
    AppLayoutForm: () => import('@/modules/apps/components/AppLayoutForm.vue')
  },

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    generalDataEditing: false,
    layoutDataEditing: false,

    generalData: null,
    layoutData: null,

    errors: null
  }),

  computed: {
    generalDataCreated: vm => !isNaN(vm.generalData.id)
  },

  mounted() {
    this.fetchApp();
  },

  methods: {
    fetchApp() {
      this.$store.dispatch('apps/showApp', this.id).then(({ data }) => {
        let cleanObject = this.clearObject(data);

        this.generalData = { ...cleanObject };
        this.layoutData = { ...cleanObject.layout };
      });
    },

    updateGeneralData() {
      let formData = new FormData();

      this.generalData = this.clearObject(this.generalData);

      Object.keys(this.generalData).forEach(key => {
        formData.append(
          key,
          Array.isArray(this.generalData[key])
            ? JSON.stringify(this.generalData[key])
            : this.generalData[key]
        );
      });

      // https://laravel.com/docs/6.x/routing#form-method-spoofing
      formData.append('_method', 'patch');

      this.$store
        .dispatch('apps/updateApp', {
          id: this.generalData.id,
          data: formData
        })
        .then(() => {
          this.generalDataEditing = false;
          this.errors = null;

          this.fetchApp();

          this.$store.commit('snackbar/setSnack', {
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });
        })
        .catch(error => {
          this.errors = error.response;
        });
    },

    updateLayoutData() {
      this.$store
        .dispatch('apps/updateLayout', {
          id: this.generalData.id,
          layout: this.layoutData
        })
        .then(() => {
          this.layoutDataEditing = false;

          this.$store.commit('snackbar/setSnack', {
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });
        });
    },

    removeFile(file) {
      let formData = new FormData();

      formData.append('file_to_remove', this.generalData[file]);
      formData.append('type', file);
      formData.append('_method', 'patch');

      this.$store
        .dispatch('apps/updateApp', {
          id: this.generalData.id,
          data: formData
        })
        .then(() => {
          this.generalData[file] = null;
          delete this.generalData[file];
        });
    },

    cancelEditing(data) {
      this[data] = false;
      this.fetchApp();
    },

    clearObject(object) {
      let cleanObject = {};

      Object.keys(object).forEach(key => {
        if (object[key] != null) cleanObject[key] = object[key];
      });

      return cleanObject;
    },

    generateUrl(item) {
      return `${process.env.VUE_APP_CORE_API}/../storage/${this.generalData[item]}`;
    }
  }
};
</script>
